import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Button, TextField, Typography, Container, Box, Grid2 as Grid, Paper, CircularProgress } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useSnackbar } from '@contexts/SnackbarContext';
import { saveToken } from '@contexts/AuthContext';
import API from '@utils/axios';
import { ROLE_INTERNAL_API } from '@utils/helpers';
import LangSelect from '@components/LangSelect';

function LoginForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const response = await API.post('/auth/login', {
        email,
        password
      });

      const { data } = response;
      if (data.access_token) {
        const decoded = jwtDecode(data.access_token);
        if (decoded.role === ROLE_INTERNAL_API) {
          throw new Error(t('This account cannot be used to log in'));
        }
        saveToken(data.access_token);
        navigate('/');
      } else {
        throw new Error(t('Invalid email or password'));
      }
    } catch (err) {
      showMessage(err.response?.data?.message
        ? t(err.response.data.message)
        : err.message,
        'error');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ width: '100%', p: 4, mt: { xs: 2, sm: 6, md: 12 } }}>

        <img src="/logo.webp" alt="Logo" style={{ width: '100%', height: 'auto', marginBottom: 16 }} />

        <Grid container>
          <Grid size="grow" xs>
            <Typography component="h1" variant="h2">
              {t("Sign in")}
            </Typography>
          </Grid>
          <Grid>
            <LangSelect
              variant="standard"
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Language' }}
            />
          </Grid>
        </Grid>

        <Box component="form" onSubmit={handleLogin} noValidate sx={{ mt: 1 }}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={t("Email Address")}
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={t("Password")}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('Sign in')
            )}
          </Button>
          <Grid container justifyContent="center">
            <Grid>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  {t('Forgot Password')}
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default LoginForm;
